package fr.manyverse.app.presentation.components.dice3d

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableLongStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Matrix
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.graphics.drawscope.withTransform
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.TextMeasurer
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import fr.manyverse.app.domain.model.DieType
import fr.manyverse.app.presentation.dice3d.ArenaSpec
import fr.manyverse.app.presentation.dice3d.DiceColor
import fr.manyverse.app.presentation.dice3d.DiceImpact
import fr.manyverse.app.presentation.dice3d.DiceOneFace
import fr.manyverse.app.presentation.dice3d.DiceRollSimulation
import fr.manyverse.app.presentation.dice3d.DiePhase
import fr.manyverse.app.presentation.dice3d.DieSimState
import fr.manyverse.app.presentation.dice3d.Face
import fr.manyverse.app.presentation.dice3d.OneFaceGlyph
import fr.manyverse.app.presentation.dice3d.Quaternion
import fr.manyverse.app.presentation.dice3d.SupportContact
import fr.manyverse.app.presentation.dice3d.Vec3
import fr.manyverse.app.presentation.dice3d.displayDiceFor
import fr.manyverse.app.presentation.dice3d.readRollResults
import fr.manyverse.app.presentation.dice3d.rollIndexForDie
import kotlin.math.ceil
import kotlin.math.sqrt
import kotlin.random.Random

/** Feutre par défaut, quand l'appelant ne dit pas de quelle couleur est sa piste. */
private val DEFAULT_FELT = Color(0xFF1B3B28)

// Lumière fixe en haut à gauche, légèrement vers le spectateur.
private val LIGHT_DIRECTION = Vec3(-0.35f, -0.55f, 0.75f).normalized()
private const val BACKFACE_EPSILON = 0.02f

/** Le glyphe apparaît à partir de cette incidence et devient opaque à partir de la seconde. */
private const val GLYPH_VISIBILITY_THRESHOLD = 0.35f
private const val GLYPH_OPAQUE_THRESHOLD = 0.72f

/** Halo des résultats critiques : rayon (en multiples du rayon du dé) et durée d'apparition. */
private const val HALO_RADIUS_FACTOR = 2.1f
private const val HALO_FADE_IN_MILLIS = 450

/**
 * Chanfrein des arêtes. Un dé n'a pas d'arêtes vives : elles sont cassées, et c'est ce liseré
 * qui accroche la lumière et donne au solide son épaisseur. La face est donc tracée deux fois —
 * le polygone plein dans la teinte du chanfrein, puis le même rentré vers son centre dans la
 * teinte de la face — ce qui laisse tout autour une bande de largeur constante en proportion.
 *
 * Le chanfrein est plus sombre que sa face : il regarde de côté, donc moins vers la lumière.
 */
private const val BEVEL_INSET = 0.09f
private const val BEVEL_SHADE = 0.34f

/**
 * Ambiante hémisphérique. Sans elle, une face détournée de la lumière tombe vers le noir et le
 * dé a l'air découpé dans du carton. Une face tournée vers le haut reçoit la lumière du ciel,
 * une face tournée vers le bas le renvoi du feutre — c'est ce second terme qui **assoit** le dé
 * dans le plateau au lieu de l'y poser comme une décalcomanie.
 */
private const val AMBIENT = 0.52f
private const val SKY_AMBIENT = 0.18f
private const val FELT_BOUNCE = 0.30f

/** Ombre de contact : rayon (en multiples du rayon du dé), aplatissement et opacité au centre. */
private const val SHADOW_RADIUS_FACTOR = 1.15f
private const val SHADOW_FLATTEN = 0.72f
private const val SHADOW_ALPHA = 0.38f

/**
 * Empreinte minimale de l'ombre, en rayons : ce que projette un dé posé sur une **pointe**. La
 * tache suit l'appui réel — large et franche sous un dé couché à plat, resserrée sous un dé
 * dressé sur un coin — au lieu d'être la même dans tous les cas.
 */
private const val SHADOW_POINT_FOOTPRINT = 0.45f

/** Hauteur, en rayons, au-delà de laquelle l'ombre se recentre sous le dé plutôt que sous son appui. */
private const val SHADOW_ANCHOR_LIFT = 1.5f

/** Étalement et pâlissement de l'ombre avec la hauteur du dé (exprimée en rayons). */
private const val MAX_SHADOW_LIFT = 6f
private const val SHADOW_SPREAD_PER_LIFT = 0.18f
private const val SHADOW_FADE_PER_LIFT = 0.55f

/**
 * Reflet spéculaire : dureté de la tache et part de blanc qu'elle ajoute au corps. L'ombrage
 * étant plat, une face entière s'allume d'un bloc — et pour un dé posé, l'œil et la lumière
 * sont presque symétriques par rapport à la verticale, donc la face résultat tombe pile dans
 * la tache. Le gain reste donc faible : on cherche un vernis, pas un éclat.
 */
private const val SPECULAR_SHININESS = 20
private const val SPECULAR_STRENGTH = 0.18f

// Emplacements dans [DiceRenderScratch.frame] : chaque vecteur du repère de la face occupe
// trois flottants consécutifs.
private const val NORMAL = 0
private const val CENTROID = 3
private const val TEXT_UP = 6
private const val TEXT_RIGHT = 9

/**
 * Tampons réutilisés d'une image à l'autre. Le rendu logiciel tourne à 60 im/s pour parfois
 * vingt dés : chaque liste intermédiaire y coûterait des milliers d'allocations par seconde,
 * et autant de travail pour le ramasse-miettes.
 */
private class DiceRenderScratch {

    /** Repère de la face courante, tourné : voir [NORMAL], [CENTROID], [TEXT_UP], [TEXT_RIGHT]. */
    val frame = FloatArray(12)

    val matrix = Matrix()

    /** Appui du dé courant : sert à caler son ombre là où il touche vraiment. */
    val support = SupportContact()

    private var screen = FloatArray(0)

    /** Sommets du dé courant projetés, en paires (x, y) écran. */
    fun screenBuffer(vertexCount: Int): FloatArray {
        if (screen.size < vertexCount * 2) screen = FloatArray(vertexCount * 2)
        return screen
    }
}

/**
 * Caméra : œil un peu en avant du bord bas du plateau, à cette distance du feutre (en
 * multiples de la plus grande dimension). Aucun dé n'est ainsi vu parfaitement de face.
 */
private const val CAMERA_Y_BIAS = 1.12f
private const val CAMERA_DISTANCE_FACTOR = 1.35f

/**
 * Caméra **commune à tous les dés** : un seul œil, placé côté spectateur. Chaque dé est
 * donc vu sous son propre angle — un dé rangé sur le côté montre ses flancs au lieu de se
 * réduire à sa face du dessus. Le plan de projection est le feutre (z = 0), où roulent les
 * centres des dés : leur position simulée est aussi leur position à l'écran.
 */
private class DiceCamera(val eyeX: Float, val eyeY: Float, val distance: Float) {

    fun project(x: Float, y: Float, z: Float): Offset {
        val scale = distance / (distance - z)
        return Offset(eyeX + (x - eyeX) * scale, eyeY + (y - eyeY) * scale)
    }

    /** Carré de la distance de l'œil au centre affiché de [state] : sert d'ordre de dessin. */
    fun squaredDistanceTo(state: DieSimState): Float {
        val dx = state.drawX - eyeX
        val dy = state.drawY - eyeY
        val dz = state.drawZ - distance
        return dx * dx + dy * dy + dz * dz
    }
}

private fun cameraFor(arena: ArenaSpec) = DiceCamera(
    eyeX = arena.width / 2f,
    eyeY = arena.height * CAMERA_Y_BIAS,
    distance = maxOf(arena.width, arena.height) * CAMERA_DISTANCE_FACTOR
)

/**
 * Hauteur du sommet le plus haut d'un dé en pleine course, en rayons — chute initiale mise à
 * part. Sert à reculer le mur du fond de ce que la perspective fera gagner au dé.
 */
private const val LEAN_HEIGHT_RADII = 1.3f

/**
 * Retrait du mur du fond. L'œil étant sous le bord bas du plateau, un dé est projeté d'autant
 * plus haut à l'écran qu'il est haut au-dessus du feutre : un dé posé contre le fond penchait
 * ainsi d'une centaine de pixels **par-dessus** le bord, et se retrouvait tronqué.
 *
 * En annulant `y_projeté = 0` pour un sommet à `z`, il vient `y ≥ r + eyeY · z / distance` :
 * le mur recule exactement de ce que la perspective ajoute, ni plus ni moins.
 */
private fun topInsetFor(width: Float, height: Float, radius: Float, wallInset: Float): Float {
    val eyeY = height * CAMERA_Y_BIAS
    val distance = maxOf(width, height) * CAMERA_DISTANCE_FACTOR
    val lean = eyeY * (LEAN_HEIGHT_RADII * radius) / distance
    // Jamais plus du tiers du plateau : sur une piste très basse, mieux vaut un débord
    // résiduel qu'une aire de jeu écrasée.
    return (wallInset + lean).coerceAtMost(height / 3f)
}

/**
 * Arène tenant compte de la bande de repos : le rayon est calculé pour que la grille finale
 * n'ait jamais à se tasser, en largeur comme en hauteur. Null tant que le canvas n'est pas mesuré.
 */
private fun arenaFor(
    canvasSize: IntSize,
    diceCount: Int,
    wallInset: Dp,
    settleTopFraction: Float,
    settleBottomFraction: Float,
    density: Density
): ArenaSpec? {
    if (canvasSize.width <= 0 || canvasSize.height <= 0 || diceCount <= 0) return null
    return with(density) {
        val width = canvasSize.width.toFloat()
        val height = canvasSize.height.toFloat()
        val inset = wallInset.toPx()
        val cols = DiceRollSimulation.gridColumns(diceCount)
        val rows = ceil(diceCount.toFloat() / cols).toInt()
        val pitch = DiceRollSimulation.SETTLE_PITCH_FACTOR
        val fitWidth = (width - 2f * inset) / (pitch * (cols - 1) + 2f)
        val bandHeight = height * (settleBottomFraction - settleTopFraction) - 2f * inset
        val fitHeight = bandHeight / (pitch * (rows - 1) + 2f)
        val radius = minOf(fitWidth, fitHeight).coerceIn(10.dp.toPx(), 44.dp.toPx())
        ArenaSpec(
            width = width,
            height = height,
            dieRadius = radius,
            wallInset = inset,
            settleTopFraction = settleTopFraction,
            settleBottomFraction = settleBottomFraction,
            topInset = topInsetFor(width, height, radius, inset)
        )
    }
}

/**
 * Rendu 3D logiciel des dés qui roulent : projection perspective, ombrage plat et glyphes
 * projetés sur les faces. La simulation est déterministe pour un [seed] donné.
 *
 * **Ce sont les dés qui décident** : aucun résultat n'est fourni en entrée, [onSettled] livre
 * les faces sur lesquelles ils se sont couchés. Si le composant quitte l'écran avant la fin,
 * le lancer est déroulé d'un coup pour produire quand même son résultat.
 */
@Composable
fun Dice3DCanvas(
    dieType: DieType,
    diceCount: Int,
    diceColor: DiceColor,
    oneFace: DiceOneFace,
    seed: Long,
    onSettled: (results: List<Int>) -> Unit,
    modifier: Modifier = Modifier,
    /** Couleur du feutre : ce que les faces tournées vers le bas reçoivent en retour. */
    feltColor: Color = DEFAULT_FELT,
    /** Halo dessiné sous un dé posé, d'après son résultat ; null pour un résultat neutre. */
    haloColorFor: (result: Int) -> Color? = { null },
    wallInset: Dp = 14.dp,
    /** Bande verticale où les dés se rangent à l'arrêt (fractions de la hauteur). */
    settleTopFraction: Float = 0f,
    settleBottomFraction: Float = 0.65f,
    /**
     * Chocs des dés, au plus un par image : le plus marquant survenu depuis la précédente. De quoi
     * faire vibrer l'appareil au rythme de ce qui se passe réellement à l'écran.
     */
    onImpact: (DiceImpact) -> Unit = {}
) {
    val density = LocalDensity.current
    var canvasSize by remember { mutableStateOf(IntSize.Zero) }
    val displayDice = remember(dieType, diceCount) { displayDiceFor(dieType, diceCount) }

    val arena = remember(
        canvasSize, displayDice, wallInset, settleTopFraction, settleBottomFraction, density
    ) {
        arenaFor(
            canvasSize = canvasSize,
            diceCount = displayDice.size,
            wallInset = wallInset,
            settleTopFraction = settleTopFraction,
            settleBottomFraction = settleBottomFraction,
            density = density
        )
    }

    // La simulation ne dépend que du lancer lui-même : un redimensionnement (rotation
    // d'écran, fenêtre, clavier) la met à l'échelle au lieu de renvoyer les dés en l'air.
    val sim = remember(seed, displayDice, arena != null) {
        arena?.let { DiceRollSimulation(dice = displayDice, arena = it, random = Random(seed)) }
    }

    var frameTick by remember { mutableLongStateOf(0L) }

    LaunchedEffect(sim, arena) {
        if (sim == null || arena == null) return@LaunchedEffect
        sim.resize(arena)
        // Un lancer déjà terminé n'a plus de boucle pour redessiner : on l'invalide ici.
        frameTick++
    }
    val currentOnSettled by rememberUpdatedState(onSettled)
    val currentOnImpact by rememberUpdatedState(onImpact)
    // Livraison unique du résultat, que la course se termine à l'écran ou d'un coup au
    // moment où le composant disparaît.
    val reported = remember(sim) { BooleanArray(1) }
    val report: (DiceRollSimulation) -> Unit = { simulation ->
        if (!reported[0]) {
            reported[0] = true
            currentOnSettled(readRollResults(dieType, simulation.landedValues))
        }
    }

    // Le halo n'apparaît qu'une fois les dés rangés dans la zone de lecture ; la boucle de
    // simulation est alors terminée, c'est l'animation qui redemande le dessin.
    var settled by remember(sim) { mutableStateOf(false) }
    // Résultats connus dès l'arrêt : ils pilotent la couleur des halos.
    var landed by remember(sim) { mutableStateOf<List<Int>>(emptyList()) }

    DisposableEffect(sim) {
        onDispose {
            // Écran quitté avant la fin : le lancer est déterministe, on le déroule d'un
            // coup pour qu'il produise quand même son résultat.
            if (sim != null && !reported[0]) {
                sim.fastForward()
                report(sim)
            }
        }
    }
    val haloProgress by animateFloatAsState(
        targetValue = if (settled) 1f else 0f,
        animationSpec = tween(durationMillis = HALO_FADE_IN_MILLIS),
        label = "diceCriticalHalo"
    )

    LaunchedEffect(sim) {
        if (sim == null) return@LaunchedEffect
        var last = 0L
        while (!sim.isSettled) {
            withFrameNanos { now ->
                // Le temps réel écoulé, brut : la simulation le consomme par pas fixes et
                // écrête elle-même les images trop longues. La cadence de l'écran décide
                // donc de la fréquence des images, jamais de la physique.
                val dt = if (last == 0L) 0f else (now - last) / 1e9f
                last = now
                sim.step(dt)
                // Les chocs des quelques pas qui viennent de passer, résumés en un seul : c'est
                // la cadence de l'écran, et non celle de la physique, qui rythme le ressenti.
                sim.drainImpact()?.let(currentOnImpact)
                frameTick++
            }
        }
        settled = true
        landed = readRollResults(dieType, sim.landedValues)
        report(sim)
    }

    val textMeasurer = rememberTextMeasurer()
    val glyphStyle = remember { TextStyle(fontSize = 64.sp, fontWeight = FontWeight.Bold) }
    val layoutCache = remember(textMeasurer) { mutableMapOf<String, TextLayoutResult>() }
    val facePath = remember { Path() }
    val scratch = remember { DiceRenderScratch() }

    // Découpe au plateau : les dés sont lâchés sous le bord bas et y entrent en volant. Sans
    // ça, un dé encore hors piste se dessinerait par-dessus les boutons du pied de page.
    Canvas(modifier = modifier.clipToBounds().onSizeChanged { canvasSize = it }) {
        frameTick // Lecture volontaire : invalide le dessin à chaque frame de simulation.
        val simulation = sim ?: return@Canvas
        val radius = simulation.arena.dieRadius
        val camera = cameraFor(simulation.arena)
        // Halos d'abord : ils restent en fond, sans jamais voiler un dé voisin. Un d100
        // critique colore ses deux moitiés, le critère portant sur le résultat combiné.
        if (haloProgress > 0f && landed.isNotEmpty()) {
            simulation.states.forEachIndexed { index, state ->
                val result = landed.getOrNull(rollIndexForDie(dieType, index))
                val halo = result?.let(haloColorFor)
                if (halo != null && state.phase == DiePhase.SETTLED) {
                    drawCriticalHalo(
                        center = Offset(state.drawX, state.drawY),
                        radius = radius,
                        color = halo,
                        progress = haloProgress
                    )
                }
            }
        }
        // Puis les ombres de contact, qui posent les dés sur le feutre au lieu de les y
        // faire flotter — toutes sous les dés, pour qu'aucune ne barre un voisin.
        simulation.states.forEach { state -> drawContactShadow(state, radius, scratch) }
        // Du plus loin de l'œil au plus proche : le plus proche est dessiné en dernier, par-dessus.
        // Ce n'est pas l'ordonnée à l'écran qui décide. La perspective écarte le haut d'un dé du
        // pied de l'œil, si bien que de deux dés posés côte à côte c'est le plus proche de ce
        // pied qui penche sur l'autre — et un dé qui saute passe devant ses voisins, alors qu'à
        // l'ordonnée près il disparaissait sous celui posé juste en dessous de lui.
        simulation.states.sortedByDescending { camera.squaredDistanceTo(it) }.forEach { state ->
            drawDie(
                state = state,
                radius = radius,
                camera = camera,
                diceColor = diceColor,
                feltColor = feltColor,
                oneFace = oneFace,
                textMeasurer = textMeasurer,
                glyphStyle = glyphStyle,
                layoutCache = layoutCache,
                facePath = facePath,
                scratch = scratch
            )
        }
    }
}

/**
 * Lueur radiale sous un dé au résultat critique : opaque au ras du dé, fondue à néant au bord
 * du halo. [progress] pilote l'apparition une fois le dé posé.
 */
private fun DrawScope.drawCriticalHalo(
    center: Offset,
    radius: Float,
    color: Color,
    progress: Float
) {
    val haloRadius = radius * HALO_RADIUS_FACTOR
    drawCircle(
        brush = Brush.radialGradient(
            colorStops = arrayOf(
                0f to color.copy(alpha = 0.55f * progress),
                0.5f to color.copy(alpha = 0.38f * progress),
                1f to Color.Transparent
            ),
            center = center,
            radius = haloRadius
        ),
        radius = haloRadius,
        center = center
    )
}

/**
 * Ombre portée sur le feutre : disque flou aplati, posé là où le rayon lumineux passant par
 * le centre du dé touche le plateau. Elle s'écarte, grandit et pâlit quand le dé monte —
 * c'est ce qui rend la chute lisible et pose le dé au lieu de le faire flotter.
 */
private fun DrawScope.drawContactShadow(state: DieSimState, radius: Float, scratch: DiceRenderScratch) {
    val height = state.drawZ
    val lift = (height / radius).coerceIn(0f, MAX_SHADOW_LIFT)
    // Où le dé touche, et sur quelle largeur : une face pose une empreinte large, une pointe
    // un simple point. La tache s'ancre sur cet appui — un dé dressé sur un coin ne projette
    // pas la même ombre qu'un dé couché.
    val support = scratch.support
    state.die.mesh.support(state.drawOrientation, support)
    var footX = 0f
    var footY = 0f
    for (index in 0 until support.count) {
        val vertex = state.drawOrientation.rotate(state.die.mesh.vertices[support.vertices[index]])
        footX += vertex.x
        footY += vertex.y
    }
    if (support.count > 0) {
        footX /= support.count
        footY /= support.count
    }
    var spread = 0f
    for (index in 0 until support.count) {
        val vertex = state.drawOrientation.rotate(state.die.mesh.vertices[support.vertices[index]])
        val dx = vertex.x - footX
        val dy = vertex.y - footY
        spread = maxOf(spread, sqrt(dx * dx + dy * dy))
    }
    // Plus le dé monte, moins son appui veut dire quelque chose : l'ombre se recentre sous lui.
    val anchored = (1f - lift / SHADOW_ANCHOR_LIFT).coerceIn(0f, 1f)
    val at = Offset(
        state.drawX + footX * radius * anchored - LIGHT_DIRECTION.x / LIGHT_DIRECTION.z * height,
        state.drawY + footY * radius * anchored - LIGHT_DIRECTION.y / LIGHT_DIRECTION.z * height
    )
    // Étalement proportionnel à la hauteur, borné pour qu'un dé haut ne noie pas le plateau.
    val footprint = SHADOW_POINT_FOOTPRINT + spread
    val shadowRadius = radius *
        ((footprint + (SHADOW_RADIUS_FACTOR - footprint) * (1f - anchored)) +
            lift * SHADOW_SPREAD_PER_LIFT)
    val alpha = SHADOW_ALPHA / (1f + lift * SHADOW_FADE_PER_LIFT)
    withTransform({ scale(scaleX = 1f, scaleY = SHADOW_FLATTEN, pivot = at) }) {
        drawCircle(
            brush = Brush.radialGradient(
                colorStops = arrayOf(
                    0f to Color.Black.copy(alpha = alpha),
                    0.55f to Color.Black.copy(alpha = alpha * 0.55f),
                    1f to Color.Transparent
                ),
                center = at,
                radius = shadowRadius
            ),
            radius = shadowRadius,
            center = at
        )
    }
}

private fun DrawScope.drawDie(
    state: DieSimState,
    radius: Float,
    camera: DiceCamera,
    diceColor: DiceColor,
    feltColor: Color,
    oneFace: DiceOneFace,
    textMeasurer: TextMeasurer,
    glyphStyle: TextStyle,
    layoutCache: MutableMap<String, TextLayoutResult>,
    facePath: Path,
    scratch: DiceRenderScratch
) {
    val q = state.drawOrientation
    val mesh = state.die.mesh
    // Sommets tournés puis projetés une fois pour toutes : les faces d'un polyèdre partagent
    // leurs sommets, et le tampon évite de reconstruire une liste à chaque image.
    val screen = scratch.screenBuffer(mesh.vertices.size)
    val frame = scratch.frame
    // Boucles indexées jusqu'au bout : un forEach sur une List alloue son itérateur, ce qui
    // se paierait par dé et par image.
    for (index in mesh.vertices.indices) {
        q.rotateInto(mesh.vertices[index], frame, 0)
        val p = camera.project(
            state.drawX + frame[0] * radius,
            state.drawY + frame[1] * radius,
            state.drawZ + frame[2] * radius
        )
        screen[index * 2] = p.x
        screen[index * 2 + 1] = p.y
    }

    // Aucun tri des faces : sur un solide convexe vu d'un point extérieur, les faces
    // conservées par le culling perspectif pavent la silhouette sans jamais se recouvrir.
    for (faceIndex in mesh.faces.indices) {
        val face = mesh.faces[faceIndex]
        q.rotateInto(face.normal, frame, NORMAL)
        q.rotateInto(face.centroid, frame, CENTROID)
        val nx = frame[NORMAL]
        val ny = frame[NORMAL + 1]
        val nz = frame[NORMAL + 2]
        // Culling perspectif : c'est la direction face → œil qui décide, pas l'axe caméra.
        // Avec une caméra partagée, un dé excentré n'est jamais vu de face.
        val ex = camera.eyeX - (state.drawX + frame[CENTROID] * radius)
        val ey = camera.eyeY - (state.drawY + frame[CENTROID + 1] * radius)
        val ez = camera.distance - (state.drawZ + frame[CENTROID + 2] * radius)
        val eyeLength = sqrt(ex * ex + ey * ey + ez * ez)
        if (eyeLength < 1e-6f) continue
        val vx = ex / eyeLength
        val vy = ey / eyeLength
        val vz = ez / eyeLength
        val facing = nx * vx + ny * vy + nz * vz
        if (facing <= BACKFACE_EPSILON) continue

        val lit = maxOf(0f, nx * LIGHT_DIRECTION.x + ny * LIGHT_DIRECTION.y + nz * LIGHT_DIRECTION.z)
        // Ambiante hémisphérique : le ciel éclaire par le haut, le feutre renvoie par le bas.
        // Une face détournée de la lumière n'est donc jamais noire, elle prend la couleur de ce
        // qu'elle regarde — et le dé s'assoit dans le plateau au lieu d'y être collé.
        val ambient = AMBIENT + SKY_AMBIENT * maxOf(0f, nz)
        val brightness = ambient + (1f - ambient) * lit
        // Reflet spéculaire (Blinn-Phong) : le corps du dé accroche la lumière comme de la
        // résine polie au lieu de rendre un aplat de carton.
        val body = lerp(Color.Black, diceColor.body, brightness)
            .let { lerp(it, feltColor, FELT_BOUNCE * maxOf(0f, -nz)) }
        val fill = lerp(body, Color.White, specular(nx, ny, nz, vx, vy, vz))

        val corners = face.indices
        // Chanfrein : la face entière dans la teinte de l'arête cassée, puis la même rentrée
        // vers son centre dans la teinte de la face. Il reste tout autour un liseré qui donne
        // au dé son épaisseur, là où un trait d'un pixel ne faisait que le détourer.
        var centerX = 0f
        var centerY = 0f
        for (corner in corners.indices) {
            centerX += screen[corners[corner] * 2]
            centerY += screen[corners[corner] * 2 + 1]
        }
        centerX /= corners.size
        centerY /= corners.size
        facePath.rewind()
        for (corner in corners.indices) {
            val vi = corners[corner]
            val px = screen[vi * 2]
            val py = screen[vi * 2 + 1]
            if (corner == 0) facePath.moveTo(px, py) else facePath.lineTo(px, py)
        }
        facePath.close()
        drawPath(facePath, lerp(fill, Color.Black, BEVEL_SHADE))
        facePath.rewind()
        for (corner in corners.indices) {
            val vi = corners[corner]
            val px = centerX + (screen[vi * 2] - centerX) * (1f - BEVEL_INSET)
            val py = centerY + (screen[vi * 2 + 1] - centerY) * (1f - BEVEL_INSET)
            if (corner == 0) facePath.moveTo(px, py) else facePath.lineTo(px, py)
        }
        facePath.close()
        drawPath(facePath, fill)

        // Les faces sans chiffre — les coiffes du d4 barillet — sont ombrées et détourées comme
        // les autres, mais rien n'y est écrit.
        val value = face.value
        if (value != null && facing > GLYPH_VISIBILITY_THRESHOLD) {
            drawFaceGlyph(
                face = face,
                orientation = q,
                facing = facing,
                state = state,
                radius = radius,
                camera = camera,
                scratch = scratch,
                fillColor = fill,
                // Le glyphe subit l'éclairage de sa face : un chiffre ne doit pas briller
                // sur un flanc à l'ombre.
                glyphColor = lerp(Color.Black, diceColor.pip, brightness),
                glyph = if (state.die.allowOneGlyph && value == 1) oneFace.glyph else null,
                label = state.die.labels[value] ?: value.toString(),
                textMeasurer = textMeasurer,
                glyphStyle = glyphStyle,
                layoutCache = layoutCache
            )
        }
    }
}

/** Terme spéculaire Blinn-Phong pour une normale et une direction d'œil unitaires. */
private fun specular(nx: Float, ny: Float, nz: Float, vx: Float, vy: Float, vz: Float): Float {
    var hx = LIGHT_DIRECTION.x + vx
    var hy = LIGHT_DIRECTION.y + vy
    var hz = LIGHT_DIRECTION.z + vz
    val length = sqrt(hx * hx + hy * hy + hz * hz)
    if (length < 1e-6f) return 0f
    hx /= length; hy /= length; hz /= length
    val cosine = maxOf(0f, nx * hx + ny * hy + nz * hz)
    var power = cosine
    repeat(SPECULAR_SHININESS - 1) { power *= cosine }
    return power * SPECULAR_STRENGTH
}

private fun DrawScope.drawFaceGlyph(
    face: Face,
    orientation: Quaternion,
    /** Incidence de la face (normale · direction de l'œil) : pilote le fondu du glyphe. */
    facing: Float,
    state: DieSimState,
    radius: Float,
    camera: DiceCamera,
    scratch: DiceRenderScratch,
    fillColor: Color,
    glyphColor: Color,
    /** Image portée par la face à la place de son chiffre ; null pour écrire [label]. */
    glyph: OneFaceGlyph?,
    label: String,
    textMeasurer: TextMeasurer,
    glyphStyle: TextStyle,
    layoutCache: MutableMap<String, TextLayoutResult>
) {
    // Attache affine rigide : les deux axes du repère de la face (textUp et sa
    // perpendiculaire) sont projetés à l'écran, et le glyphe est dessiné dans ce
    // repère projeté. Il subit ainsi le même raccourci perspectif que sa face —
    // sans ça, le chiffre semble pivoter sur lui-même quand le dé roule.
    // Le centroïde a déjà été tourné par l'appelant, dans le même tampon.
    val frame = scratch.frame
    orientation.rotateInto(face.textUp, frame, TEXT_UP)
    orientation.rotateInto(face.textRight, frame, TEXT_RIGHT)
    val cx = state.drawX + frame[CENTROID] * radius
    val cy = state.drawY + frame[CENTROID + 1] * radius
    val cz = state.drawZ + frame[CENTROID + 2] * radius
    val c2 = camera.project(cx, cy, cz)
    // Pas d'échantillonnage exprimé en unités modèle : les vecteurs écran restent donc
    // en pixels par unité modèle, comme l'attend [glyphModelSize].
    val epsilon = 0.25f
    val step = epsilon * radius
    val upScreen = (camera.project(
        cx + frame[TEXT_UP] * step, cy + frame[TEXT_UP + 1] * step, cz + frame[TEXT_UP + 2] * step
    ) - c2) * (1f / epsilon)
    val rightScreen = (camera.project(
        cx + frame[TEXT_RIGHT] * step, cy + frame[TEXT_RIGHT + 1] * step, cz + frame[TEXT_RIGHT + 2] * step
    ) - c2) * (1f / epsilon)
    // Le glyphe est pleinement opaque bien avant l'incidence frontale : avec la caméra
    // partagée, la face résultat d'un dé excentré est vue de biais et doit rester nette.
    val alpha = ((facing - GLYPH_VISIBILITY_THRESHOLD) /
        (GLYPH_OPAQUE_THRESHOLD - GLYPH_VISIBILITY_THRESHOLD)).coerceIn(0f, 1f)
    // Taille du glyphe en unités modèle (fraction du cercle inscrit de la face).
    val glyphModelSize = face.inradius * 1.2f

    // Convention Compose : vecteurs-lignes, p' = p·M — la ligne 0 est l'image de
    // l'axe x local (« droite »), la ligne 1 celle de l'axe y local (« bas » = −textUp),
    // la ligne 3 la translation.
    fun faceFrameMatrix(localToModel: Float): Matrix = scratch.matrix.also { m ->
        m.reset()
        m[0, 0] = rightScreen.x * localToModel
        m[0, 1] = rightScreen.y * localToModel
        m[1, 0] = -upScreen.x * localToModel
        m[1, 1] = -upScreen.y * localToModel
        m[3, 0] = c2.x
        m[3, 1] = c2.y
    }

    if (glyph != null) {
        withTransform({
            transform(faceFrameMatrix(localToModel = glyphModelSize / 2f))
            translate(0f, glyph.centerOffsetY)
        }) {
            drawPath(glyph.fill, glyphColor, alpha = alpha)
            glyph.hollows?.let { drawPath(it, fillColor, alpha = alpha) }
        }
    } else {
        val layout = layoutCache.getOrPut(label) { textMeasurer.measure(label, glyphStyle) }
        val maxDim = maxOf(layout.size.width, layout.size.height).toFloat()
        if (maxDim <= 0f) return
        withTransform({
            transform(faceFrameMatrix(localToModel = glyphModelSize / maxDim))
        }) {
            drawText(
                textLayoutResult = layout,
                color = glyphColor,
                topLeft = Offset(-layout.size.width / 2f, -layout.size.height / 2f),
                alpha = alpha
            )
        }
    }
}
